/**
 * Declining a question as not needed: why, in a line, then the button (Enter
 * in the line records it too). A decision on the record — who, when and why
 * — that the agent reads at its next run and asks no more about; never a
 * dismissal. An identity question has no such answer: "not sure" is its own.
 */

import { useState } from 'react';

import { Button, Group, TextInput } from '@mantine/core';

export function NotNeeded({
  onDecline,
}: Readonly<{
  /** Records `{ not_needed: true, note: why }`. */
  onDecline: (why: string) => Promise<unknown>;
}>) {
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = why.trim() !== '' && !busy;

  const decline = async () => {
    if (!ready) {
      return;
    }
    setBusy(true);
    try {
      await onDecline(why.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Group gap={6} wrap="nowrap" data-testid="not-needed">
      <TextInput
        size="xs"
        placeholder="Not needed? Say why"
        aria-label="Why it isn't needed"
        value={why}
        onChange={(e) => setWhy(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            void decline();
          }
        }}
        style={{ flex: 1 }}
        data-testid="not-needed-why"
      />
      <Button
        size="xs"
        variant="default"
        onClick={() => void decline()}
        disabled={!ready}
        loading={busy}
        data-testid="not-needed-record"
      >
        Not needed
      </Button>
    </Group>
  );
}
